import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { SimpleButton } from '../../../../components/buttons/SimpleButton';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import { LinkCell } from '../../../../components/tables/cellComponents/LinkCell';
import { FilterField, FilterForm } from '../../../../components/tables/filterForms/FilterForm';
import { SearchField } from '../../../../components/tables/filterForms/SearchField';
import SFCTable from '../../../../components/tables/SFCTable';
import { useToast } from '../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../hooks';
import {
  useDeleteEnrollmentMutation,
  useGetAllEnrollmentsQuery
} from '../../../../services/endpoints/schedule/enrollment';
import { Enrollment } from '../../../../types/enrollment.type';
import { getFilterValue } from '../../../../utils/listFilterSearchParams';
import { getErrorMessage } from '../../../../utils/utils';

const defaultScope = 'all';

const DeleteEnrollmentButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteEnrollment, { isLoading }] = useDeleteEnrollmentMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteEnrollment(id).unwrap();
      addToast('Enrollment deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Enrollment"
        message="Are you sure you want to delete this enrollment?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

export const enrollmentColumns: ColumnDef<Enrollment>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    enableSorting: true,
  },
  {
    header: 'Session',
    accessorKey: 'course_id',
    id: 'session_title',
    enableSorting: true,
    cell: ({ row, getValue }) => (
      <LinkCell
        content={row.original.session_title}
        url={`/admin/schedule/sessions/${getValue<number>()}`}
      />
    )
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/enrollments/${row.original.id}`}
          underline
        />
        <LinkCell
          content="Edit"
          url={`/admin/enrollments/${row.original.id}/edit`}
          underline
        />
        <DeleteEnrollmentButton id={row.original.id} />
      </Stack>
    )
  }
];
// TODO: add status = 'open' for filtering, check if assign instructor column is required

export const EnrollmentsView = () => {
  const { addToast } = useToast();
  const [filters, setFilters] = useListFiltersInUrl(
    { scope: defaultScope },
    { omitScopeFromUrl: true }
  );
  const { data, error, isLoading, isFetching } = useGetAllEnrollmentsQuery(filters);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const navigate = useNavigate();

  const filterFormFields: FilterField[] = [
    {
      name: 'session_title',
      element: (
        <SearchField
          name="session_title"
          label="Session"
          placeholder="Search session"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'session_title')
    }
  ];

  useEffect(() => {
    if (!isLoading && error) {
      addToast('Error while loading enrollments', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setEnrollments(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev?.data,
        filterBy
      }
    }));
  };

  return (
    <div className="py-2">
      <SFCTable
        name={`Enrollments`}
        columns={enrollmentColumns}
        data={enrollments}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        isLoading={isLoading}
        isFetching={isFetching}
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
        newItemEl={
          <SimpleButton
            variant="sfc-blueberry"
            height={40}
            truncate={false}
            onClick={() => navigate('/admin/enrollments/new')}
          >
            New enrollment
          </SimpleButton>
        }
      />
    </div>
  );
};
